// 动作键现在是后台可配置的任意字符串（idle/walk/...或自定义）。
export type PetActionKey = string;
export type BackendActionStatus = "queued" | "generating" | "complete" | "error";
export type BackendJobStatus = "queued" | "processing" | "complete" | "error";

export interface GenerationVideoClip {
  key: PetActionKey;
  label: string;
  taskId: string;
  videoUrl: string;
  proxyVideoUrl?: string;
  sourceVideoUrl?: string;
}

export interface GenerationJobAction {
  key: PetActionKey;
  label: string;
  status: BackendActionStatus;
  video?: GenerationVideoClip;
  error?: string;
  regenerateUsed?: boolean;
}

export interface GenerationJob {
  jobId: string;
  petId: string;
  status: BackendJobStatus;
  progress: number;
  message: string;
  actions: GenerationJobAction[];
}

export interface SkinPackageStatus {
  petId: string;
  status: "waiting" | "building" | "ready" | "error";
  version: string;
  skinId?: string | null;
  downloadUrl?: string | null;
  sha256?: string | null;
  message: string;
}

export interface PetPhotoAsset {
  assetId: string;
  sourceImageUrl: string;
  cutoutImageUrl: string;
  originalFileName: string;
  contentType: string;
  fileSize: number;
  message: string;
}

export interface PaymentQuota {
  generationCredits: number;
  petLimit: number;
  petCount: number;
  canCreatePet: boolean;
}

export interface AccountUser {
  userId: string;
  region: "CN" | "GLOBAL";
  email?: string | null;
  nickname?: string | null;
  avatarUrl?: string | null;
}

export interface AccountPet {
  petId: string;
  petName: string;
  userAlias: string;
  status: string;
  currentSkinId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccountState {
  user: AccountUser;
  quota: PaymentQuota;
  pets: AccountPet[];
}

export interface PaymentProduct {
  productId: string;
  productType: "credits" | "pet_slot";
  title: string;
  description: string;
  region: string;
  credits: number;
  petSlots: number;
  amountCny: number;
  currency: string;
  status: string;
}

export interface PaymentOrder {
  orderId: string;
  productId: string;
  title: string;
  channel: PaymentChannel;
  status: "pending" | "paid" | "failed" | "closed" | "refunded";
  credits: number;
  petSlots: number;
  amountCny: number;
  currency: string;
  providerTradeNo?: string | null;
  quota?: PaymentQuota | null;
  paymentUrl?: string | null;
  message: string;
}

export interface DesktopBindingCode {
  bindingId: string;
  petId: string;
  pairingCode: string;
  expiresAt: string;
  message: string;
}

export interface CreateGenerationJobInput {
  photoAssetId: string;
  petId?: string;
  petName: string;
  userAlias: string;
  actions: PetActionKey[];
  customDescription: string;
}

import { appLocale, t } from "./i18n";
import { buildApiUrl, runtimeConfig, type PaymentChannel } from "./runtime-config";

export class ApiRequestError extends Error {
  constructor(message: string, public readonly status: number, public readonly code?: string) {
    super(message);
    this.name = "ApiRequestError";
  }
}

export function isUnauthorizedApiError(error: unknown): boolean {
  return error instanceof ApiRequestError && error.status === 401;
}

export async function uploadPetPhoto(file: File): Promise<PetPhotoAsset> {
  const body = new FormData();
  body.set("photo", file);

  return requestJson<PetPhotoAsset>("/api/pet-photo-assets", {
    method: "POST",
    body
  });
}

export async function createGenerationJob(input: CreateGenerationJobInput): Promise<GenerationJob> {
  const body = new FormData();
  body.set("photoAssetId", input.photoAssetId);
  if (input.petId) {
    body.set("petId", input.petId);
  }
  body.set("petName", input.petName);
  body.set("userAlias", input.userAlias);
  body.set("actions", JSON.stringify(input.actions));
  body.set("customDescription", input.customDescription);
  body.set("locale", appLocale);

  return requestJson<GenerationJob>("/api/generation/jobs", {
    method: "POST",
    headers: {
      "Idempotency-Key": createIdempotencyKey()
    },
    body
  });
}

export async function getGenerationJob(jobId: string): Promise<GenerationJob> {
  return requestJson<GenerationJob>(`/api/generation/jobs/${encodeURIComponent(jobId)}`);
}

export async function getLatestGenerationJobForPet(petId: string): Promise<GenerationJob | null> {
  const response = await request(`/api/pets/${encodeURIComponent(petId)}/latest-generation-job`);
  if (response.status === 204) {
    return null;
  }

  const json = await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 401) {
      handleUnauthorizedResponse();
    }
    throw new ApiRequestError(json?.message || `HTTP ${response.status}`, response.status, json?.error);
  }

  return json as GenerationJob;
}

export interface GenerationAction {
  key: string;
  name: string;
  isDefault: boolean;
  creditCost: number;
  sortOrder: number;
}

export async function listGenerationActions(): Promise<GenerationAction[]> {
  const query = new URLSearchParams({
    locale: appLocale
  });
  return requestJson<GenerationAction[]>(`/api/generation/actions?${query.toString()}`);
}

export async function regenerateGenerationAction(jobId: string, actionKey: PetActionKey): Promise<GenerationJob> {
  return requestJson<GenerationJob>(`/api/generation/jobs/${encodeURIComponent(jobId)}/actions/${actionKey}/regenerate`, {
    method: "POST",
    headers: {
      "Idempotency-Key": createIdempotencyKey()
    }
  });
}

export async function syncGenerationJob(jobId: string): Promise<GenerationJob> {
  return requestJson<GenerationJob>(`/api/generation/jobs/${encodeURIComponent(jobId)}/sync`, {
    method: "POST"
  });
}

export async function getPetPackageStatus(petId: string): Promise<SkinPackageStatus> {
  return requestJson<SkinPackageStatus>(`/api/pets/${encodeURIComponent(petId)}/package-status`);
}

export async function getCurrentAccount(): Promise<AccountState> {
  return requestJson<AccountState>("/api/me");
}

export async function getPaymentProducts(productType?: "credits" | "pet_slot"): Promise<PaymentProduct[]> {
  const query = new URLSearchParams();
  if (productType) {
    query.set("type", productType);
  }
  return requestJson<PaymentProduct[]>(`/api/payments/products?${query.toString()}`);
}

export async function createPaymentOrder(productId: string, channel: PaymentChannel): Promise<PaymentOrder> {
  return requestJson<PaymentOrder>("/api/payments/orders", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Idempotency-Key": createIdempotencyKey()
    },
    body: JSON.stringify({ productId, channel })
  });
}

export async function getPaymentOrder(orderId: string): Promise<PaymentOrder> {
  return requestJson<PaymentOrder>(`/api/payments/orders/${encodeURIComponent(orderId)}`);
}

export async function createDesktopBinding(petId: string): Promise<DesktopBindingCode> {
  return requestJson<DesktopBindingCode>("/api/desktop-bindings", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ petId })
  });
}

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await request(path, init);
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 401) {
      handleUnauthorizedResponse();
    }
    throw new ApiRequestError(json?.message || `HTTP ${response.status}`, response.status, json?.error);
  }
  return json as T;
}

async function request(path: string, init?: RequestInit): Promise<Response> {
  const token = localStorage.getItem("pet.auth.token");
  const headers = new Headers(init?.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  headers.set("X-App-Locale", appLocale);
  headers.set("X-App-Region", runtimeConfig.market.region);
  const response = await safeFetch(path, {
    ...init,
    headers
  });
  return response;
}

async function safeFetch(path: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(buildApiUrl(path), init);
  } catch {
    throw new ApiRequestError(t("store.network"), 0, "network_error");
  }
}

function createIdempotencyKey() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `idem-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function handleUnauthorizedResponse() {
  clearAuthStorage(localStorage);
  if (typeof sessionStorage !== "undefined") {
    clearAuthStorage(sessionStorage);
  }

  const currentLocation = globalThis.location;
  if (currentLocation?.pathname !== "/login") {
    currentLocation?.assign?.("/login");
  }
}

function clearAuthStorage(storage: Storage) {
  const keysToRemove: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key) {
      continue;
    }

    const normalizedKey = key.toLowerCase();
    if (normalizedKey === "pet.auth.token" || normalizedKey.startsWith("pet.auth.") || normalizedKey.includes("jwt")) {
      keysToRemove.push(key);
    }
  }

  for (const key of keysToRemove) {
    storage.removeItem(key);
  }
}
